import { useMemo } from 'react';
import sanitize from 'sanitize-html';
import { useFormContext, useWatch } from 'react-hook-form';
import { useAppSelector } from '../../../../../hooks';
import { useGetCompaniesQuery } from '../../../../../redux/api/companiesApi';
import {
  ApplicantRole,
  ApplicationType,
  MoreApplicationsChoice,
  UnionRole,
} from '../../../../../types/PartyApplication/partyApplication';
import { DID_NOT_FIND_VALUE } from 'components/formComponents/Base/Dropdown';
import { useWorksites } from '../../../../ApplicationV3/hooks/useWorksites';
import { useGetUnionsQuery } from '../../../partyApplicationApi';
import { PartApplicationFormInput } from '../../../validation/formSchema';
import {
  EmploymentType,
  TerminatedEmployeeInput,
} from '../ReportDismissedSection/types';
import styles from './PrintablePartyApplication.module.scss';

type PrintableRow = {
  label: string;
  value: string;
};

type ConsentItem = {
  id: string;
  html: string;
};

const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return true;
};

const toText = (value: unknown): string => {
  return hasValue(value) ? String(value).trim() : '';
};

const toSectionRows = (rows: PrintableRow[]): PrintableRow[] => {
  return rows.filter((row) => hasValue(row.value));
};

const toDisplayValue = (value: string): string => {
  return hasValue(value) ? value.trim() : '-';
};

const PrintablePartyApplication = () => {
  const { pageTexts } = useAppSelector((state) => state.partyApplication);
  const { control } = useFormContext<PartApplicationFormInput>();
  const formData = useWatch({ control });
  const partyOrgNumber = toText(formData?.partyRegardingApplication);
  const selectedWorksiteId = toText(formData?.worksiteRegardingApplication);
  const selectedUnionId = toText(formData?.unionOrganization);
  const isManualWorksite = selectedWorksiteId === DID_NOT_FIND_VALUE;

  const { data: companies } = useGetCompaniesQuery(partyOrgNumber, {
    skip: partyOrgNumber.length === 0,
  });
  const { worksites } = useWorksites(partyOrgNumber || undefined);
  const { data: unions } = useGetUnionsQuery();

  const selectedCompanyName = useMemo(() => {
    return (
      companies?.find((company) => company.OrgNr === partyOrgNumber)
        ?.Företagsnamn ?? ''
    );
  }, [companies, partyOrgNumber]);

  const selectedWorksiteName = useMemo(() => {
    if (isManualWorksite) {
      return toText(formData?.worksitename);
    }

    return (
      worksites?.find((worksite) => worksite.id === selectedWorksiteId)?.name ??
      ''
    );
  }, [formData?.worksitename, isManualWorksite, selectedWorksiteId, worksites]);

  const selectedUnionName = useMemo(() => {
    return (
      unions?.find((union) => union.value === selectedUnionId)?.label ?? ''
    );
  }, [selectedUnionId, unions]);

  const dateText = useMemo(() => {
    return new Intl.DateTimeFormat('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  }, []);

  const applicationTypeText = useMemo(() => {
    const value = Number(formData?.applicationType);

    if (value === ApplicationType.dismissedDueToRedundancyOrContractExpiry) {
      return (
        pageTexts?.startTexts?.dismissedDueToRedundancyOrContractExpiryRadioBtn
          ?.label ?? 'Uppsagda pga arbetsbrist eller avslutade tidsbegränsade'
      );
    }

    if (value === ApplicationType.companyBankruptcy) {
      return (
        pageTexts?.startTexts?.companyBankruptcyRadioBtn?.label ??
        'Företagskonkurs'
      );
    }

    if (value === ApplicationType.dismissedDueToIllnessOrInjury) {
      return (
        pageTexts?.startTexts?.dismissedDueToIllnessOrInjuryRadioBtn?.label ??
        'Uppsagd pga sjukdom'
      );
    }

    return '';
  }, [formData?.applicationType, pageTexts]);

  const applicantRoleText = useMemo(() => {
    const value = Number(formData?.applicantRole);

    if (value === ApplicantRole.companyRepresentative) {
      return (
        pageTexts?.startTexts?.companyRepresentative?.label ??
        'Företagsrepresentant'
      );
    }

    if (value === ApplicantRole.unionRepresentative) {
      return (
        pageTexts?.startTexts?.unionRepresentative?.label ??
        'Facklig representant'
      );
    }

    return '';
  }, [formData?.applicantRole, pageTexts]);

  const unionRoleText = useMemo(() => {
    const value = Number(formData?.unionRole);

    if (value === UnionRole.UnionRepresentative) {
      return 'Ombudsman (anställd på fackförbundet)';
    }

    if (value === UnionRole.UnionLocalRepresentative) {
      return 'Förtroendevald (anställd på företaget)';
    }

    return '';
  }, [formData?.unionRole]);

  const moreApplicationsText = useMemo(() => {
    const value = Number(formData?.moreApplications);

    if (value === MoreApplicationsChoice.Yes) {
      return pageTexts?.startTexts?.moreApplicationsYesRadioBtn?.label ?? 'Ja';
    }

    if (value === MoreApplicationsChoice.No) {
      return pageTexts?.startTexts?.moreApplicationsNoRadioBtn?.label ?? 'Nej';
    }

    return '';
  }, [formData?.moreApplications, pageTexts]);

  const selectedConsents = useMemo((): ConsentItem[] => {
    const consentEntries = Object.entries(formData?.consent ?? {});
    const labelsById = new Map(
      (pageTexts?.submitSection?.requiredConsentCheckboxes ?? []).map(
        (item) => [String(item.id), item.label]
      )
    );

    return consentEntries
      .filter(([, isChecked]) => isChecked)
      .map(([id]) => {
        const label = labelsById.get(id) ?? id;
        return {
          id,
          html: sanitize(String(label), {
            allowedTags: ['strong', 'em', 'br', 'ul', 'ol', 'li', 'p', 'a'],
            allowedAttributes: {
              a: ['href', 'target', 'rel'],
            },
            allowedSchemes: ['http', 'https', 'mailto'],
          }),
        };
      })
      .filter((item) => hasValue(item.html));
  }, [formData?.consent, pageTexts]);

  const startSectionRows = useMemo(() => {
    return toSectionRows([
      { label: 'Typ av ansökan', value: applicationTypeText },
      {
        label: pageTexts?.startTexts?.applicantRoleSubHeadline ?? 'Min roll',
        value: applicantRoleText,
      },
      {
        label:
          pageTexts?.startTexts?.moreApplicationsSubHeadline ??
          'Kopplad till flera projekt',
        value: moreApplicationsText,
      },
    ]);
  }, [applicationTypeText, applicantRoleText, moreApplicationsText, pageTexts]);

  const partySectionRows = useMemo(() => {
    return toSectionRows([
      {
        label: 'Företag',
        value: selectedCompanyName,
      },
      {
        label: 'Arbetsställe',
        value: selectedWorksiteName,
      },
      {
        label:
          pageTexts?.partyRegardingApplicationSection?.worksiteAddress?.label ??
          'Arbetsställets adress',
        value: isManualWorksite ? toText(formData?.worksiteAddress) : '',
      },
      {
        label:
          pageTexts?.partyRegardingApplicationSection?.worksitePostalCode
            ?.label ?? 'Arbetsställets postnummer',
        value: isManualWorksite ? toText(formData?.worksitePostalCode) : '',
      },
      {
        label:
          pageTexts?.partyRegardingApplicationSection?.worksitePostalCity
            ?.label ?? 'Arbetsställets ort',
        value: isManualWorksite ? toText(formData?.worksitePostalCity) : '',
      },
      {
        label:
          pageTexts?.partyRegardingApplicationSection?.worksiteTelephone
            ?.label ?? 'Arbetsställets telefon',
        value: isManualWorksite ? toText(formData?.worksiteTelephone) : '',
      },
    ]);
  }, [
    formData,
    isManualWorksite,
    pageTexts,
    selectedCompanyName,
    selectedWorksiteName,
  ]);

  const companySectionRows = useMemo(() => {
    return toSectionRows([
      {
        label:
          pageTexts?.companyDetailsSection?.companyContactFirstname?.label ??
          'Förnamn',
        value: toText(formData?.companyContactFirstname),
      },
      {
        label:
          pageTexts?.companyDetailsSection?.companyContactLastname?.label ??
          'Efternamn',
        value: toText(formData?.companyContactLastname),
      },
      {
        label:
          pageTexts?.companyDetailsSection?.companyContactTelephone?.label ??
          'Telefon',
        value: toText(formData?.companyContactPhone),
      },
      {
        label:
          pageTexts?.companyDetailsSection?.companyContactEmail?.label ??
          'E-post',
        value: toText(formData?.companyContactEmail),
      },
      {
        label:
          pageTexts?.companyDetailsSection?.companyContactAddress?.label ??
          'Adress',
        value: toText(formData?.companyContactAddress),
      },
      {
        label:
          pageTexts?.companyDetailsSection?.companyContactPostalCode?.label ??
          'Postnummer',
        value: toText(formData?.companyContactPostalCode),
      },
      {
        label:
          pageTexts?.companyDetailsSection?.companyContactCity?.label ?? 'Ort',
        value: toText(formData?.companyContactCity),
      },
    ]);
  }, [formData, pageTexts]);

  const unionSectionRows = useMemo(() => {
    return toSectionRows([
      {
        label:
          pageTexts?.unionSection?.unionOrganization?.label ?? 'Fackförbund',
        value: selectedUnionName,
      },
      { label: 'Facklig roll', value: unionRoleText },
      {
        label:
          pageTexts?.unionSection?.unionContactFirstname?.label ?? 'Förnamn',
        value: toText(formData?.unionContactFirstname),
      },
      {
        label:
          pageTexts?.unionSection?.unionContactLastname?.label ?? 'Efternamn',
        value: toText(formData?.unionContactLastname),
      },
      {
        label:
          pageTexts?.unionSection?.unionContactTelephone?.label ?? 'Telefon',
        value: toText(formData?.unionContactPhone),
      },
      {
        label: pageTexts?.unionSection?.unionContactEmail?.label ?? 'E-post',
        value: toText(formData?.unionContactEmail),
      },
      {
        label: pageTexts?.unionSection?.unionContactAddress?.label ?? 'Adress',
        value: toText(formData?.unionContactAddress),
      },
      {
        label:
          pageTexts?.unionSection?.unionContactPostalCode?.label ??
          'Postnummer',
        value: toText(formData?.unionContactPostalCode),
      },
      {
        label: pageTexts?.unionSection?.unionContactCity?.label ?? 'Ort',
        value: toText(formData?.unionContactCity),
      },
    ]);
  }, [formData, pageTexts, selectedUnionName, unionRoleText]);

  const otherInfoRows = useMemo(() => {
    return toSectionRows([
      {
        label:
          pageTexts?.otherInfoSection?.otherInfoComment?.label ?? 'Kommentar',
        value: toText(formData?.otherInfo),
      },
    ]);
  }, [formData?.otherInfo, pageTexts]);

  const printableEmployees = useMemo(() => {
    const employees = (formData?.terminatedEmployees ??
      []) as TerminatedEmployeeInput[];
    return employees.filter((employee) =>
      Object.values(employee).some(hasValue)
    );
  }, [formData?.terminatedEmployees]);

  const hasAnyData =
    startSectionRows.length > 0 ||
    partySectionRows.length > 0 ||
    companySectionRows.length > 0 ||
    unionSectionRows.length > 0 ||
    printableEmployees.length > 0 ||
    otherInfoRows.length > 0 ||
    selectedConsents.length > 0;

  return (
    <article className={styles.printRoot}>
      <header className={styles.header}>
        <h1>Ansökan om omställningsstöd</h1>
        <p>Utskriftsdatum: {dateText}</p>
      </header>

      {startSectionRows.length > 0 && (
        <section className={styles.section}>
          <h2>Ansökningens grunduppgifter</h2>
          <dl className={styles.list}>
            {startSectionRows.map((row) => (
              <div key={row.label} className={styles.row}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {partySectionRows.length > 0 && (
        <section className={styles.section}>
          <h2>Företag och arbetsställe</h2>
          <dl className={styles.list}>
            {partySectionRows.map((row) => (
              <div key={row.label} className={styles.row}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {companySectionRows.length > 0 && (
        <section className={styles.section}>
          <h2>Företagets kontaktperson</h2>
          <dl className={styles.list}>
            {companySectionRows.map((row) => (
              <div key={row.label} className={styles.row}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {unionSectionRows.length > 0 && (
        <section className={styles.section}>
          <h2>Fackförbund och kontaktuppgifter</h2>
          <dl className={styles.list}>
            {unionSectionRows.map((row) => (
              <div key={row.label} className={styles.row}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {printableEmployees.length > 0 && (
        <section className={styles.section}>
          <h2>Registrerade uppsagda</h2>
          <table className={styles.employeesTable}>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Personnummer</th>
                <th scope="col">Förnamn</th>
                <th scope="col">Efternamn</th>
                <th scope="col">Anst.form</th>
                <th scope="col">Startdatum</th>
                <th scope="col">Uppsagd</th>
                <th scope="col">Slutdatum</th>
                <th scope="col">E-post</th>
                <th scope="col">Mobil</th>
              </tr>
            </thead>
            <tbody>
              {printableEmployees.map((employee, index) => {
                const employmentTypeText =
                  employee.employmentType ===
                  String(EmploymentType.Tillsvidareanstlld)
                    ? 'Tillsvidare'
                    : employee.employmentType ===
                        String(EmploymentType.Tidsbegrnsatanstlld)
                      ? 'Tidsbegränsad'
                      : '-';

                return (
                  <tr
                    key={`${employee.socialSecurityNumber || 'person'}-${index}`}
                  >
                    <td>{index + 1}</td>
                    <td>{toDisplayValue(employee.socialSecurityNumber)}</td>
                    <td>{toDisplayValue(employee.firstName)}</td>
                    <td>{toDisplayValue(employee.lastName)}</td>
                    <td>{employmentTypeText}</td>
                    <td>{toDisplayValue(employee.startDate)}</td>
                    <td>{toDisplayValue(employee.terminationDate)}</td>
                    <td>{toDisplayValue(employee.endDate)}</td>
                    <td>{toDisplayValue(employee.email)}</td>
                    <td>{toDisplayValue(employee.phone)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      {otherInfoRows.length > 0 && (
        <section className={styles.section}>
          <h2>Övrigt</h2>
          <dl className={styles.list}>
            {otherInfoRows.map((row) => (
              <div key={row.label} className={styles.row}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {selectedConsents.length > 0 && (
        <section className={styles.section}>
          <h2>Godkända intyganden</h2>
          <ul className={styles.consentList}>
            {selectedConsents.map((item) => (
              <li
                key={item.id}
                dangerouslySetInnerHTML={{ __html: item.html }}
              />
            ))}
          </ul>
        </section>
      )}

      {!hasAnyData && (
        <section className={styles.section}>
          <p>Inga uppgifter är ifyllda än.</p>
        </section>
      )}
    </article>
  );
};

export default PrintablePartyApplication;
